import React, { useCallback, useState } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RowButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { RadioButtonPanels } from '@eika/radio-button';

import { EikaModal } from '../../../components/EikaModal';
import { StickyFooterRow } from '../../../components/shared/StickyFooter';
import FieldSkeleton from '../../../components/transaction/FieldSkeleton';
import { salaryAccount, useAccounts } from '../../../fetchHooks/useAccount';
import type { AccountNumber } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';

interface Props {
    selectedAccount?: AccountNumber;
    setSelectedAccount: (account?: AccountNumber) => void;
    error?: string;
}

const SalaryAccountField: React.FC<Props> = ({ selectedAccount, setSelectedAccount, error }) => {
    const translated = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const { data: accounts, isLoading } = useAccounts({ select: salaryAccount });
    const onChangeAccount = useCallback(
        (accountNumber: string) => setSelectedAccount({ value: accountNumber }),
        [setSelectedAccount],
    );

    if (isLoading) {
        return <FieldSkeleton />;
    }

    return (
        <>
            <RowButton onClick={() => setShowModal(true)}>
                <RowButton.AccountSelectorLayout
                    placeholder={translated.salaryInfo.edit.account.placeholder}
                    label={translated.salaryInfo.edit.account.label}
                    accountAlias={
                        accounts?.find((account) => account.accountNumber.value === selectedAccount?.value)?.alias
                    }
                    accountNumber={selectedAccount && formatAccountNumber(selectedAccount)}
                    error={error}
                    icon={IconTypes.LITEN_PIL_HOEYRE}
                />
            </RowButton>
            <EikaModal id="salary-account-field-modal" open={showModal} setOpen={setShowModal}>
                {accounts && (
                    <RadioButtonPanels
                        label={translated.salaryInfo.edit.account.title}
                        sublabel={translated.salaryInfo.edit.account.subtitle}
                        onChange={onChangeAccount}
                        options={accounts.map((account) => ({
                            value: account.accountNumber.value,
                            label: account.alias,
                            description: formatAccountNumber(account.accountNumber),
                        }))}
                        selectedOption={selectedAccount?.value}
                    />
                )}
                <StickyFooterRow>
                    <StandardButton onClick={() => setShowModal(false)}>{translated.continue}</StandardButton>
                </StickyFooterRow>
            </EikaModal>
        </>
    );
};

export default SalaryAccountField;
